import { Box, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import Tags from 'modules/documents/components/DocumentTemplates/Tags'
import { DocumentTemplate as NullableDocumentTemplate } from 'modules/documents/hooks/useGetDocumentTemplate'

type DocumentTemplate = NonNullable<NullableDocumentTemplate>

interface Props {
  documentTemplates: DocumentTemplate[]
  onSelect: (documentTemplate: DocumentTemplate) => void
  value: string | null | undefined
  label?: string
  placeholder?: string
  disabled?: boolean
}

type IndexedItems = {
  [key: string]: DocumentTemplate
}

function DocumentTemplatesSelector({
  documentTemplates,
  onSelect,
  value,
  label,
  placeholder = i18n.t('split_documents.templates.create_modal.no_template_option'),
  disabled = false,
}: Props) {
  const indexedItems: IndexedItems = useUnsafeMemo(() => {
    const indexedTemplates: IndexedItems = {}

    documentTemplates.forEach((documentTemplate) => {
      if (!documentTemplate.id) return

      indexedTemplates[documentTemplate.id] = documentTemplate
    })

    return indexedTemplates
  }, [documentTemplates])

  return (
    <Box gap="s4">
      <Selector.Popover
        placeholder={placeholder}
        searchPlaceholder={i18n.t('common.search')}
        value={[value]}
        label={label}
        blankLabel={i18n.t('documents.document_templates.selector.empty')}
        mandatory
        onChange={(val) => {
          const selected = indexedItems[String(val)]

          selected && onSelect(selected)
        }}
        isDisabled={disabled}
      >
        {documentTemplates.map((documentTemplate) => {
          const isChecked = documentTemplate.id === value
          const textColor = isChecked ? 'viridian1200' : 'grey1000'

          return (
            <Selector.Option
              key={documentTemplate.id}
              value={documentTemplate.id}
              searchValue={documentTemplate.name}
              label={documentTemplate.name}
              showSeparator={false}
            >
              <Box alignItems="flexStart" gap="s4" paddingBottom="s4">
                <Text.Normal size="300" color={textColor}>
                  {documentTemplate.name}
                </Text.Normal>
                <Tags documentTemplate={documentTemplate} />
              </Box>
            </Selector.Option>
          )
        })}
      </Selector.Popover>
    </Box>
  )
}

export default DocumentTemplatesSelector
